/**
 * Identity-fact frequency guard primitives (Law 3). A durable memory fact or
 * identity phrase ("the kids", "bedtime ritual") repeated across an output
 * reads as a machine working the user's file, so the copy agents cap each
 * salient fact at one mention per surface. This module owns the generic
 * pieces: deriving the salient token set from a memory/identity block and
 * matching mentions. No hardcoded phrases; the tokens come from the input.
 */
const RAW_COMMON_WORDS = [
    // Pronouns, determiners, question words.
    "i",
    "me",
    "my",
    "mine",
    "you",
    "your",
    "yours",
    "yourself",
    "he",
    "him",
    "his",
    "himself",
    "she",
    "her",
    "hers",
    "herself",
    "it",
    "its",
    "itself",
    "we",
    "us",
    "our",
    "ours",
    "they",
    "them",
    "their",
    "theirs",
    "themselves",
    "this",
    "that",
    "these",
    "those",
    "who",
    "whom",
    "whose",
    "which",
    "what",
    "someone",
    "something",
    "anyone",
    "anything",
    "everyone",
    "everything",
    "nobody",
    "nothing",
    "own",
    "self",
    // Auxiliaries and high-frequency verbs.
    "am",
    "is",
    "are",
    "was",
    "were",
    "be",
    "been",
    "being",
    "have",
    "has",
    "had",
    "having",
    "do",
    "does",
    "did",
    "doing",
    "done",
    "will",
    "would",
    "shall",
    "should",
    "can",
    "could",
    "may",
    "might",
    "must",
    "let",
    "lets",
    "letting",
    "get",
    "got",
    "getting",
    "go",
    "goes",
    "going",
    "gone",
    "went",
    "come",
    "coming",
    "came",
    "make",
    "making",
    "made",
    "take",
    "taking",
    "took",
    "taken",
    "keep",
    "keeping",
    "kept",
    "want",
    "wanted",
    "wanting",
    "need",
    "needed",
    "needing",
    "feel",
    "feeling",
    "felt",
    "know",
    "knowing",
    "knew",
    "known",
    "think",
    "thinking",
    "thought",
    "see",
    "seeing",
    "saw",
    "seen",
    "look",
    "looking",
    "looked",
    "say",
    "saying",
    "said",
    "tell",
    "telling",
    "told",
    "try",
    "trying",
    "tried",
    "use",
    "using",
    "used",
    "find",
    "finding",
    "found",
    "give",
    "giving",
    "gave",
    "given",
    "put",
    "putting",
    "start",
    "starting",
    "started",
    "stop",
    "stopping",
    "stopped",
    "finish",
    "finishing",
    "finished",
    "complete",
    "completing",
    "completed",
    "help",
    "helping",
    "helped",
    "show",
    "showing",
    "showed",
    "shown",
    "work",
    "working",
    "worked",
    "provide",
    "providing",
    "provided",
    "include",
    "including",
    "included",
    "become",
    "becoming",
    "became",
    "stay",
    "staying",
    "stayed",
    "remain",
    "remaining",
    "remained",
    "aim",
    "aiming",
    "aimed",
    "focus",
    "focusing",
    "focused",
    "track",
    "tracking",
    "tracked",
    "record",
    "recording",
    "recorded",
    "respond",
    "responded",
    "response",
    "answer",
    "answered",
    "ignore",
    "ignoring",
    "ignored",
    "send",
    "sending",
    "sent",
    "read",
    "reading",
    "readings",
    "improve",
    "improving",
    "improved",
    "fall",
    "falling",
    "fell",
    "move",
    "moving",
    "moved",
    "run",
    "running",
    "ran",
    "build",
    "building",
    "built",
    "pick",
    "picking",
    "picked",
    "set",
    "setting",
    "prioritize",
    "prioritizing",
    "prioritized",
    "prioritise",
    "prioritising",
    "prioritised",
    "categorize",
    "categorized",
    "categorise",
    "categorised",
    "base",
    "based",
    "remember",
    "remembers",
    "remembered",
    "enjoy",
    "enjoying",
    "enjoyed",
    "indicate",
    "indicating",
    "indicated",
    "request",
    "requested",
    "grounded",
    "grounding",
    // Prepositions, conjunctions, particles.
    "in",
    "on",
    "at",
    "by",
    "for",
    "with",
    "without",
    "from",
    "to",
    "of",
    "off",
    "over",
    "under",
    "into",
    "onto",
    "out",
    "up",
    "down",
    "through",
    "during",
    "before",
    "after",
    "between",
    "among",
    "about",
    "around",
    "near",
    "since",
    "until",
    "while",
    "than",
    "then",
    "and",
    "or",
    "but",
    "nor",
    "so",
    "if",
    "because",
    "as",
    "though",
    "although",
    "when",
    "whenever",
    "where",
    "wherever",
    "how",
    "however",
    "why",
    "not",
    "no",
    "yes",
    "only",
    "both",
    "either",
    "neither",
    "each",
    "every",
    "any",
    "some",
    "few",
    "more",
    "most",
    "less",
    "least",
    "much",
    "many",
    "several",
    "such",
    "same",
    "other",
    "another",
    "again",
    "once",
    "twice",
    "per",
    "like",
    "the",
    "a",
    "an",
    "too",
    "also",
    "all",
    "yet",
    "none",
    "else",
    "rather",
    "across",
    "against",
    "ago",
    "almost",
    "already",
    "okay",
    "thing",
    "way",
    "lot",
    "bit",
    "fact",
    "term",
    "kind",
    "sort",
    "part",
    "place",
    "shape",
    // Common adverbs and adjectives.
    "very",
    "really",
    "quite",
    "just",
    "still",
    "even",
    "ever",
    "never",
    "always",
    "often",
    "usually",
    "sometimes",
    "rarely",
    "recently",
    "recent",
    "currently",
    "current",
    "regular",
    "regularly",
    "daily",
    "weekly",
    "monthly",
    "nightly",
    "overall",
    "general",
    "generally",
    "likely",
    "probably",
    "actually",
    "especially",
    "particularly",
    "mostly",
    "mainly",
    "early",
    "earlier",
    "earliest",
    "late",
    "later",
    "latest",
    "last",
    "first",
    "second",
    "third",
    "next",
    "new",
    "old",
    "good",
    "better",
    "best",
    "bad",
    "worse",
    "worst",
    "high",
    "higher",
    "highest",
    "low",
    "lower",
    "lowest",
    "big",
    "bigger",
    "small",
    "smaller",
    "short",
    "shorter",
    "long",
    "longer",
    "fast",
    "faster",
    "slow",
    "slower",
    "steady",
    "steadier",
    "strong",
    "stronger",
    "weak",
    "weaker",
    "easy",
    "easier",
    "hard",
    "harder",
    "soon",
    "sooner",
    "well",
    "fine",
    "great",
    "little",
    "full",
    "whole",
    "half",
    "active",
    "today",
    "tonight",
    "tomorrow",
    "yesterday",
    "now",
    "ahead",
    "away",
    "back",
    "here",
    "there",
    "together",
    "enough",
    "quiet",
    "warm",
    "cool",
    "personal",
    "durable",
    "salient",
    // Spelled numbers (spoken-format transcripts use these constantly).
    "zero",
    "one",
    "two",
    "three",
    "four",
    "five",
    "six",
    "seven",
    "eight",
    "nine",
    "ten",
    "eleven",
    "twelve",
    "thirteen",
    "fourteen",
    "fifteen",
    "sixteen",
    "seventeen",
    "eighteen",
    "nineteen",
    "twenty",
    "thirty",
    "forty",
    "fifty",
    "sixty",
    "seventy",
    "eighty",
    "ninety",
    "hundred",
    "thousand",
    "point",
    // Product and health-domain vocabulary every brief legitimately reuses.
    "health",
    "healthos",
    "thrive",
    "user",
    "data",
    "signal",
    "signals",
    "sleep",
    "sleeping",
    "slept",
    "asleep",
    "bed",
    "bedtime",
    "night",
    "morning",
    "evening",
    "afternoon",
    "day",
    "week",
    "month",
    "year",
    "hour",
    "minute",
    "moment",
    "time",
    "energy",
    "rest",
    "rested",
    "recovery",
    "heart",
    "rate",
    "blood",
    "pressure",
    "water",
    "hydration",
    "drink",
    "glass",
    "step",
    "steps",
    "walk",
    "walking",
    "movement",
    "activity",
    "exercise",
    "food",
    "meal",
    "eat",
    "eating",
    "stress",
    "calm",
    "breath",
    "breathe",
    "breathing",
    "body",
    "wearable",
    "device",
    "phone",
    "screen",
    "goal",
    "plan",
    "habit",
    "routine",
    "reminder",
    "notification",
    "microstep",
    "heartbeat",
    "brief",
    "app",
    "onboarding",
    "weather",
    "hrv",
    "rhr",
    "average",
    "baseline",
    "usual",
    "typical",
    "context",
    "pattern",
    "preference",
    "surfaced",
    "milestone",
    "horizon",
    "summary",
    "transcript",
    "title",
    "subtitle",
];
function normalise(word) {
    const w = word
        .toLowerCase()
        .replace(/'s$/, "")
        .replace(/[^a-z-]/g, "");
    if (w.length > 3 && w.endsWith("s") && !w.endsWith("ss"))
        return w.slice(0, -1);
    return w;
}
const COMMON_WORDS = new Set(RAW_COMMON_WORDS.flatMap((w) => [w, normalise(w)]));
const WORD_RE = /[a-z][a-z']*(?:-[a-z']+)*/g;
function tokenBases(text) {
    const out = new Set();
    for (const match of text.toLowerCase().matchAll(WORD_RE)) {
        const base = normalise(match[0]);
        if (base.length >= 3)
            out.add(base);
    }
    return out;
}
/**
 * Derive the salient identity/memory tokens from a rendered memory block:
 * content words that are neither common English nor product/health vocabulary
 * nor present in the excluded data blocks (signals, baselines). These are the
 * tokens a surface may mention at most once.
 */
export function salientIdentityTokens(source, exclude = []) {
    if (!source || source.trim().length === 0)
        return new Set();
    const excluded = new Set();
    for (const text of exclude) {
        if (!text)
            continue;
        for (const base of tokenBases(text))
            excluded.add(base);
    }
    const out = new Set();
    for (const base of tokenBases(source)) {
        if (COMMON_WORDS.has(base))
            continue;
        if (excluded.has(base))
            continue;
        out.add(base);
    }
    return out;
}
const RE_ESCAPE = /[.*+?^${}()|[\]\\]/g;
/** Word-boundaried matcher for a salient token, tolerant of plural/possessive forms. */
export function identityTokenRe(base) {
    return new RegExp(`\\b${base.replace(RE_ESCAPE, "\\$&")}(?:'s|s|es)?\\b`, "i");
}
/** Sentence split shared by the frequency guards (terminator + whitespace, or newline). */
export function splitIntoSentences(text) {
    return text
        .split(/(?<=[.!?])\s+|\n+/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
}
/** Indices of the sentences mentioning the token; a repeat is two or more. */
export function tokenMentionIndices(sentences, base) {
    const re = identityTokenRe(base);
    const out = [];
    for (let i = 0; i < sentences.length; i++) {
        const sentence = sentences[i];
        if (sentence !== undefined && re.test(sentence))
            out.push(i);
    }
    return out;
}
//# sourceMappingURL=identity-facts.js.map